// Tiny data layer: fetch + polling hook. No axios, no router, no chart lib.
import { useEffect, useRef, useState } from 'react';

export async function getJSON<T>(path: string): Promise<T> {
  const r = await fetch(`/api/v1/${path}`, { headers: { Accept: 'application/json' } });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

/** Poll an endpoint every `ms`, exposing data / error state. */
export function usePoll<T>(path: string, ms: number) {
  const [data, setData] = useState<T | null>(null);
  const [err, setErr] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const d = await getJSON<T>(path);
        if (alive.current) { setData(d); setErr(false); }
      } catch {
        if (alive.current) setErr(true);
      }
      if (alive.current) timer = setTimeout(tick, ms);
    };
    tick();
    return () => { alive.current = false; clearTimeout(timer); };
  }, [path, ms]);
  return { data, err };
}

// ---- API shapes (trimmed to fields the UI reads) ----
export interface AlpacaStatus {
  enabled: boolean;
  account: {
    equity: string; last_equity: string; initial_equity: string;
    cash: string; status: string;
  };
  positions: {
    symbol: string; qty: string; side: string; avg_entry_price: string;
    current_price: string; unrealized_pl: string; unrealized_plpc: string;
  }[];
}
export interface AlpacaHistory {
  equity_history: { timestamp: number; equity: number }[];
}
export interface WinRate {
  all_time: { wins: number; losses: number; win_rate: number; realized_pnl: number };
  today: { wins: number; losses: number; realized_pnl: number };
  recent_closed: {
    symbol: string; side: string; qty: number; entry_price: number;
    exit_price: number; pnl: number; exit_time: string; reason: string;
  }[];
}
export interface Health {
  status: string;
  subsystems: Record<string, { ok: boolean }>;
  ingestion?: { last_ingestion?: string };
}
export interface Signal {
  id: string;
  prediction_confidence: number | null;
  prediction_direction: string | null;
  event_classification: string | null;
  sentiment_market: number | null;
  tickers: string[] | null;
  importance_score: number | null;
  created_at: string;
}

export const fmtUsd = (v: number, sign = false) =>
  `${sign && v > 0 ? '+' : ''}${v < 0 ? '\u2212' : ''}$${Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

export const fmtAgo = (iso: string) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 90) return `${Math.max(1, Math.round(s))}s`;
  if (s < 5400) return `${Math.round(s / 60)}m`;
  if (s < 172800) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

/** US regular session, UTC (DST convention used repo-wide). */
export const marketOpen = () => {
  const n = new Date();
  const d = n.getUTCDay();
  if (d === 0 || d === 6) return false;
  const h = n.getUTCHours() + n.getUTCMinutes() / 60;
  return h >= 13.5 && h < 20;
};
